import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuthContext } from '@/contexts/authContext';
import { UserRole } from '@/model/User.model';
import { publicRoutes } from '@/router/routes';

/**
 * A custom hook that enforces route protection based on user authentication and roles.
 * It redirects users to appropriate routes if they are not logged in or lack the required roles.
 *
 * @param roles - An array of `UserRole` values representing the roles required to access the route.
 *
 * @remarks
 * - If the user is not logged in, they will be redirected to the login page.
 * - If the user is logged in but does not have the required role, they will be redirected to the home page.
 * - If the user is logged in and has the required role, no redirection occurs.
 *
 * @example
 * ```tsx
 * const roles = [UserRole.SchoolGroupAdmin, UserRole.SchoolAdmin];
 * useProtectedRoute(roles);
 * ```
 */
export const useProtectedRouteRoleGuard = (roles: UserRole[]) => {
  const { user, isLogin, isAuthenticating } = useAuthContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticating) {
      return;
    }

    if (isLogin && user) {
      // User is logged in but does not have the required role
      if (roles.length > 0 && !roles.includes(user.role)) {
        navigate(publicRoutes.Home.getRoute());
        return;
      }

      // User is logged in and has the required role
      return;
    }

    // User is not logged in
    navigate(publicRoutes.Login.getRoute());
  }, [isAuthenticating, isLogin, navigate, roles, user]);

  return {
    isAuthenticating,
    isLogin,
    user,
  };
};
